import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Avatar
} from '@mui/material';
import { StructuredCriteria } from '../../../types/dataTypes';

interface StructuredCriteriaCardsProps {
  criteria: StructuredCriteria[];
  title?: string;
}

const StructuredCriteriaCards: React.FC<StructuredCriteriaCardsProps> = ({ 
  criteria, 
  title = "Senior Energy Expert" 
}) => {
  if (criteria.length === 0) return null;

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      {/* Header */}
      <Typography 
        variant="h5" 
        sx={{ 
          color: '#4CAF50', 
          fontWeight: 'bold',
          mb: 3,
          textAlign: 'center'
        }}
      >
        {title}
      </Typography>

      {/* Table */}
      <TableContainer 
        component={Paper} 
        elevation={3}
        sx={{ 
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid #e0e0e0'
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell 
                sx={{ 
                  fontWeight: 'bold', 
                  fontSize: '1rem',
                  color: '#333',
                  borderBottom: '2px solid #4CAF50',
                  width: '200px'
                }}
              >
                Category
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 'bold', 
                  fontSize: '1rem',
                  color: '#333',
                  borderBottom: '2px solid #4CAF50'
                }}
              >
                Requirements
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {criteria.map((criterion, index) => (
              <TableRow 
                key={index}
                sx={{ 
                  '&:nth-of-type(odd)': { 
                    backgroundColor: '#fafafa' 
                  },
                  '&:hover': { 
                    backgroundColor: '#f0f8f0',
                    cursor: 'pointer'
                  },
                  transition: 'background-color 0.2s ease'
                }}
              >
                <TableCell 
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    verticalAlign: 'top',
                    py: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {/* Custom Emoji Avatar */}
                    <Avatar 
                      sx={{ 
                        width: 36, 
                        height: 36, 
                        backgroundColor: '##e5e5e5',
                        color: 'white',
                        fontSize: '1.2rem'
                      }}
                    >
                      {criterion.icon || '📝'}
                    </Avatar>
                    
                    {/* Category Chip */}
                    <Chip
                      label={criterion.key}
                      variant="outlined"
                      size="small"
                      sx={{
                        fontWeight: 'bold',
                        borderColor: '##e5e5e5',
                        color: '##e5e5e5',
                        backgroundColor: 'transparent',
                        maxWidth: '140px',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        },
                        '&:hover': {
                          backgroundColor: '##e5e5e510'
                        }
                      }}
                    />
                  </Box>
                </TableCell>
                
                <TableCell 
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    verticalAlign: 'top',
                    py: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    {/* Small emoji indicator for mobile/compact view */}
                    <Typography 
                      sx={{ 
                        fontSize: '1rem',
                        display: { xs: 'inline', sm: 'none' },
                        mt: 0.2
                      }}
                    >
                      {criterion.icon}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        lineHeight: 1.6,
                        color: '#333',
                        whiteSpace: 'pre-line',
                        flex: 1
                      }}
                    >
                      {criterion.value}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer with count */}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {criteria.length} requirement{criteria.length !== 1 ? 's' : ''} defined
        </Typography>
      </Box>
    </Box>
  );
};

export default StructuredCriteriaCards;